import { App, Avatar, Button, Divider, Form, Skeleton, Tooltip } from 'antd';
import clsx from 'clsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserRoleEnum } from '../../../enums/UserRoleEnum';
import type Group from '../../../interfaces/Group';
import type User from '../../../interfaces/User';
//import { SettingService } from '../../../services/settingApiService';
import { FileService } from '../../../services/fileApiService';
import ListyMetaRow from '../../Common/ListyMetaRow';
import ListyWithEmpty from '../../Common/ListyWithEmpty';
import UserMultiSelect, { type UserMultiSelectProps } from '../../Common/UserMultiSelect';
import { Icon } from '../../Core/Icon';
dayjs.extend(utc);

interface Props {
  tagId?: number;
  onSubmit: (
    users: number[],
    groups: number[],
    unsavedUsers?: User[],
    unsavedGroups?: Group[],
  ) => void;
  onCancel: () => void;
  userMultiSelectProps?: Partial<UserMultiSelectProps>;
  unsavedUsers?: User[];
  unsavedGroups?: Group[];
}

interface TagUserGroupItem {
  id: number;
  isGroup: boolean;
  name: string;
  members: number;
  userName: string;
  firstName: string;
  lastName: string;
}

interface FormState {
  users: User[];
  groups: Group[];
  tagUserGroupItemsToRemove: TagUserGroupItem[];
}

//const settingService = new SettingService();
const fileService = new FileService();

/**
 * Generates user initials for avatar display.
 * Returns first letter of firstName + first letter of lastName if both exist,
 * otherwise returns first letter of userName.
 *
 * @param firstName - User's first name
 * @param lastName - User's last name
 * @param userName - User's username
 * @returns Uppercase initials string
 */
const getUserInitials = (firstName?: string, lastName?: string, userName?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (userName) {
    return userName.charAt(0).toUpperCase();
  }
  return '';
};

export const TagUserGroupMultiSelectForm = ({
  tagId,
  onSubmit,
  onCancel,
  userMultiSelectProps,
  unsavedUsers = [],
  unsavedGroups = [],
}: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [tagUserGroupItems, setTagUserGroupItems] = useState<TagUserGroupItem[]>([]);
  const [form, setForm] = useState<FormState>({
    users: [],
    groups: [],
    tagUserGroupItemsToRemove: [],
  });

  //const [hasMore, setHasMore] = useState(true);
  //const [totalTagUsers, setTotalTagUsers] = useState(0);
  const [filters, _] = useState<any>({
    offset: 0,
    count: 10,
    search: '',
    id: tagId || 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const scrollableDivRef = useRef<HTMLDivElement>(null);

  /** Fetches file access information for the selected files.
   *
   * This function retrieves file access information for the files specified in the `selectedFiles` array.
   * It uses the `getFileAccesses` method from the `fileService` to fetch the data.
   *
   * Dependencies:
   * - `filters`: The current filters used for fetching file access information.
   */
  const getTagUsersAndGroups = useCallback(() => {
    // If the settingId is not provided, return
    if (!tagId || tagId === 0) {
      setIsLoading(false);
      return;
    }

    const payload: any = {
      ...filters,
      id: tagId || 0,
    };

    fileService
      .getTagUsers(payload.id)
      .then((response) => {
        const userExceptions = response.usersTag.map((exception: any) => ({
          id: exception.id,
          isGroup: false,
          name: exception.name,
          members: exception.members,
          userName: exception.userName,
          firstName: exception.firstName,
          lastName: exception.lastName,
        }));
        const groupExceptions = response.groupsTag.map((exception: any) => ({
          id: exception.id,
          isGroup: true,
          name: exception.name,
          members: exception.members,
          userName: exception.userName,
          firstName: exception.firstName,
          lastName: exception.lastName,
        }));

        const tagUserGroupItems = [...userExceptions, ...groupExceptions];
        setTagUserGroupItems(tagUserGroupItems);

        // if (filters.offset === 0) {
        //   setFileAccesses(users);
        // } else {
        //   setFileAccesses((prev) => [...prev, ...users]);
        // }
        // setTotalFileAccesses(response?.total || 0);
        // if (response?.accesses?.length < filters.count) {
        //   setHasMore(false);
        // } else {
        //   setHasMore(true);
        // }
      })
      .catch(() => {
        message.error('Error fetching setting exceptions');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filters]);

  /**
   * Scroll event handler: if user is near the bottom of the list and hasMore is true,
   * increment the offset to load more items. We also check isLoading to avoid double-fetches.
   */
  // const handleScroll = () => {
  //   if (!scrollableDivRef.current) return;
  //   if (isLoading || !hasMore) return;

  //   const { scrollTop, clientHeight, scrollHeight } = scrollableDivRef.current;
  //   // e.g., 50px from the bottom:
  //   if (scrollTop + clientHeight >= scrollHeight - 50) {
  //     // Load next chunk
  //     setFilters((prev: any) => ({
  //       ...prev,
  //       offset: prev.offset + prev.count,
  //     }));
  //     setIsLoading(true);
  //   }
  // };

  /**
   * Handles the submission of the form.
   *
   * This function creates arrays of user and group IDs based on the form state,
   * filters out items marked for removal, includes existing items from the API,
   * and calls the `onSubmit` callback with the complete array of items.
   *
   * Dependencies:
   */
  const handleSubmit = () => {
    // Get all items marked for removal
    const toRemoveIds = new Set(form.tagUserGroupItemsToRemove.map((item) => item.id));

    // Convert existing tagUserGroupItems (from API) to User/Group objects
    // and filter out those marked for removal
    const existingUsers: User[] = tagUserGroupItems
      .filter((item) => !item.isGroup && !toRemoveIds.has(item.id))
      .map((item) => ({
        id: item.id,
        username: item.userName,
        firstName: item.firstName,
        lastName: item.lastName,
        email: '', // Not available from API response
      }));

    const existingGroups: Group[] = tagUserGroupItems
      .filter((item) => item.isGroup && !toRemoveIds.has(item.id))
      .map((item) => ({
        id: item.id,
        name: item.name,
        usersCount: item.members,
      }));

    // Filter out unsaved items that are marked for removal
    const unsavedUsersKept = unsavedUsers.filter((user) => !toRemoveIds.has(user.id));
    const unsavedGroupsKept = unsavedGroups.filter((group) => !toRemoveIds.has(group.id));

    // Get IDs of all kept items (existing + unsaved) to avoid duplicates
    const keptUserIds = new Set([
      ...existingUsers.map((u) => u.id),
      ...unsavedUsersKept.map((u) => u.id),
    ]);
    const keptGroupIds = new Set([
      ...existingGroups.map((g) => g.id),
      ...unsavedGroupsKept.map((g) => g.id),
    ]);

    // Add newly selected users/groups that are not already in kept items
    const newUsers = form.users.filter((user) => !keptUserIds.has(user.id));
    const newGroups = form.groups.filter((group) => !keptGroupIds.has(group.id));

    // Combine all users and groups (existing + unsaved kept + newly selected)
    const finalUsers = [...existingUsers, ...unsavedUsersKept, ...newUsers];
    const finalGroups = [...existingGroups, ...unsavedGroupsKept, ...newGroups];

    // Extract IDs for the callback (remove duplicates)
    const userIds = Array.from(new Set(finalUsers.map((user) => user.id)));
    const groupIds = Array.from(new Set(finalGroups.map((group) => group.id)));

    onSubmit(userIds, groupIds, finalUsers, finalGroups);
  };

  /**
   * Handles changes to form fields.
   *
   * This function updates the form state with the new value for a specified field.
   * It takes a field name and a value, and updates the form state accordingly.
   *
   * Dependencies:
   * - `form`: The current form state.
   * - `field`: The field to update.
   * - `value`: The new value for the field.
   */
  const handleFormFieldChange = (field: keyof FormState, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * Converts unsaved users and groups to SettingExceptionItem format.
   * Filters out items that are already in saved exceptions.
   */
  const unsavedExceptionItems = useMemo(() => {
    // Get IDs of already saved exceptions to filter them out
    const savedExceptionIds = new Set(tagUserGroupItems.map((item) => item.id));

    // Filter unsaved users that are not already in saved exceptions
    const unsavedUsersFiltered = unsavedUsers.filter((user) => !savedExceptionIds.has(user.id));

    // Filter unsaved groups that are not already in saved exceptions
    const unsavedGroupsFiltered = unsavedGroups.filter((group) => !savedExceptionIds.has(group.id));

    // Map users to SettingExceptionItem format
    const userItems: TagUserGroupItem[] = unsavedUsersFiltered.map((user) => ({
      id: user.id,
      isGroup: false,
      name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || '',
      members: 0,
      userName: user.username || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
    }));

    // Map groups to SettingExceptionItem format
    const groupItems: TagUserGroupItem[] = unsavedGroupsFiltered.map((group) => ({
      id: group.id,
      isGroup: true,
      name: group.name,
      members: group.usersCount || 0,
      userName: '',
      firstName: '',
      lastName: '',
    }));

    return [...userItems, ...groupItems];
  }, [unsavedUsers, unsavedGroups, tagUserGroupItems]);

  /**
   * Checks if a file access is marked for removal.
   *
   * This function determines whether a file access exists in the list of file accesses to be removed.
   *
   * Dependencies:
   * - Relies on the `fileAccessesToRemove` array, which contains the file accesses marked for removal.
   *
   */
  const isTagUserGroupItemMarkedToRemove = useCallback(
    (tagUserGroupItem: TagUserGroupItem) => {
      return form?.tagUserGroupItemsToRemove?.find((f) => f.id === tagUserGroupItem.id);
    },
    [form.tagUserGroupItemsToRemove],
  );

  /**
   * Checks if new users or groups are selected.
   *
   * This function determines whether new users or groups are selected in the file share form.
   *
   * Dependencies:
   * - `users`: An array of selected users.
   * - `groups`: An array of selected groups.
   *
   * Returns:
   * - `true` if either `users` or `groups` is not empty.
   * - `false` otherwise.
   */
  const areNewUsersOrGroupsSelected = useMemo(() => {
    return form.users.length > 0 || form.groups.length > 0;
  }, [form.users, form.groups]);

  /**
   * Checks if the submit button is disabled.
   *
   * This function determines whether the submit button should be disabled.
   *
   * Dependencies:
   * - `isLoading`: Whether the form is loading.
   * - `areNewUsersOrGroupsSelected`: Whether new users or groups are selected.
   */
  const isSubmitButtonDisabled = useMemo(() => {
    if (isLoading) return true;

    if (areNewUsersOrGroupsSelected) {
      return form.users.length === 0 && form.groups.length === 0;
    }

    const hasAnyAccessChange =
      form.tagUserGroupItemsToRemove.length > 0 || unsavedExceptionItems.length > 0;

    return !hasAnyAccessChange;
  }, [
    isLoading,
    areNewUsersOrGroupsSelected,
    form.users.length,
    form.groups.length,
    form.tagUserGroupItemsToRemove.length,
    unsavedExceptionItems.length,
  ]);

  /**
   * Returns an array of user IDs that are ignored by the user multi select.
   *
   * This function creates an array of user IDs that are ignored by the user multi select.
   * It filters the setting exception items to only include user items and then maps the IDs.
   *
   * Dependencies:
   * - `settingExceptionItems`: The current setting exception items.
   */
  // const ignoredUserIds = useMemo(() => {
  //   return settingExceptionItems.filter((item) => !item.isGroup).map((item) => item.id);
  // }, [settingExceptionItems]);

  // Note: We don't initialize form state with unsavedUsers/unsavedGroups from userMultiSelectProps
  // because unsaved items should only appear in the list, not as selected values in UserMultiSelect

  // Filter out unsaved items from userMultiSelectProps to prevent them from appearing in UserMultiSelect
  const filteredUserMultiSelectProps = useMemo(() => {
    if (!userMultiSelectProps) return undefined;

    const unsavedUserIds = new Set(unsavedUsers.map((u) => u.id));
    const unsavedGroupIds = new Set(unsavedGroups.map((g) => g.id));

    const filtered: Partial<UserMultiSelectProps> = { ...userMultiSelectProps };

    // Filter selectedUserIds to exclude unsaved users
    if (filtered.selectedUserIds) {
      filtered.selectedUserIds = filtered.selectedUserIds.filter((id) => !unsavedUserIds.has(id));
    }

    // Filter selectedGroupIds to exclude unsaved groups
    if (filtered.selectedGroupIds) {
      filtered.selectedGroupIds = filtered.selectedGroupIds.filter(
        (id) => !unsavedGroupIds.has(id),
      );
    }

    // Filter selectedUsers to exclude unsaved users
    if (filtered.selectedUsers) {
      filtered.selectedUsers = filtered.selectedUsers.filter(
        (user) => !unsavedUserIds.has(user.id),
      );
    }

    // Filter selectedGroups to exclude unsaved groups
    if (filtered.selectedGroups) {
      filtered.selectedGroups = filtered.selectedGroups.filter(
        (group) => !unsavedGroupIds.has(group.id),
      );
    }

    return filtered;
  }, [userMultiSelectProps, unsavedUsers, unsavedGroups]);

  useEffect(() => {
    getTagUsersAndGroups();
  }, [getTagUsersAndGroups]);

  return (
    <Form
      layout="vertical"
      size="large"
      className="pt-6! [&_.ant-form-item]:mb-2! [&_.ant-form-item-explain-error]:hidden! [&_.ant-form-item-label]:hidden!"
      onFinish={handleSubmit}
    >
      <div className="flex flex-col gap-2">
        <UserMultiSelect
          className="w-full p-0"
          //ignoredUserIds={ignoredUserIds}
          size="large"
          closeOnSelect
          maxTagCount={10}
          allowClear={false}
          disabled={isLoading}
          // Only pass form state values, not unsaved items
          // Unsaved items are shown separately in the list below
          selectedUsers={form.users}
          selectedGroups={form.groups}
          selectedUserIds={form.users.map((user) => user?.id)}
          selectedGroupIds={form.groups.map((group) => group?.id)}
          onUserChange={(_, values) => {
            handleFormFieldChange('users', values);
          }}
          onGroupChange={(_, values) => handleFormFieldChange('groups', values)}
          additionalUserFilters={{
            roleFilter: [UserRoleEnum.Contributor, UserRoleEnum.Reader, UserRoleEnum.External],
          }}
          {...filteredUserMultiSelectProps}
        />
      </div>

      {/* Unsaved Users and Groups Section */}
      {unsavedExceptionItems.length > 0 && (
        <div className="flex flex-col gap-2">
          <Divider />
          <div className="flex items-center justify-between">
            <span className="text-lg font-medium">
              {t('modal.tagUserGroupMultiSelectForm.unsavedUsersAndGroups') ||
                'Unsaved Users and Groups'}
            </span>
          </div>
          <OverlayScrollbarsComponent
            options={{
              scrollbars: {
                autoHide: 'leave',
              },
            }}
            defer
            style={{ height: '100%', overflow: 'auto' }}
          >
            <div className="mb-4 flex max-h-80 flex-col overflow-y-auto pr-2">
              <ListyWithEmpty<TagUserGroupItem>
                items={unsavedExceptionItems}
                emptyText={t('common.none')}
                rowKey={(item) => `${item.isGroup ? 'g' : 'u'}-${item.id}`}
                itemRender={(unsavedTagUserGroupItem) => {
                  const isMarkedToRemove =
                    isTagUserGroupItemMarkedToRemove(unsavedTagUserGroupItem);
                  return (
                    <ListyMetaRow
                      avatar={
                        <Avatar
                          size={42}
                          className={clsx(
                            isMarkedToRemove ? 'bg-red-dark!' : 'bg-yellow!',
                            'uppercase!',
                          )}
                        >
                          {!unsavedTagUserGroupItem.isGroup ? (
                            getUserInitials(
                              unsavedTagUserGroupItem.firstName,
                              unsavedTagUserGroupItem.lastName,
                              unsavedTagUserGroupItem.userName,
                            )
                          ) : (
                            <Icon name="UsersIcon" className="h-5 w-5" />
                          )}
                        </Avatar>
                      }
                      title={
                        <span className={clsx(isMarkedToRemove && 'line-through', 'font-medium')}>
                          {unsavedTagUserGroupItem.isGroup
                            ? unsavedTagUserGroupItem.name
                            : `${unsavedTagUserGroupItem.firstName} ${unsavedTagUserGroupItem.lastName}`}
                        </span>
                      }
                      description={
                        <div className="flex h-6 flex-row items-center justify-between gap-2">
                          <Tooltip
                            title={
                              unsavedTagUserGroupItem.isGroup
                                ? unsavedTagUserGroupItem.name
                                : unsavedTagUserGroupItem.userName
                            }
                          >
                            <span className="truncate text-sm">
                              {unsavedTagUserGroupItem.isGroup
                                ? `${unsavedTagUserGroupItem.members} ${t('common.users')}`
                                : unsavedTagUserGroupItem.userName}
                            </span>
                          </Tooltip>
                        </div>
                      }
                      actions={
                        isMarkedToRemove ? (
                          <Button
                            variant="text"
                            color="primary"
                            size="small"
                            onClick={() =>
                              handleFormFieldChange(
                                'tagUserGroupItemsToRemove',
                                form.tagUserGroupItemsToRemove.filter(
                                  (f) => f.id !== unsavedTagUserGroupItem.id,
                                ),
                              )
                            }
                          >
                            {t('common.cancel')}
                          </Button>
                        ) : (
                          <div className="flex flex-row items-center gap-0.5">
                            <Tooltip placement="top" title={t('common.remove')}>
                              <Button
                                onClick={() => {
                                  handleFormFieldChange('tagUserGroupItemsToRemove', [
                                    ...form.tagUserGroupItemsToRemove,
                                    unsavedTagUserGroupItem,
                                  ]);
                                }}
                                danger
                                type="text"
                                size="small"
                                aria-label={t('a11y.removeException')}
                              >
                                <Icon name="TrashIcon" className="h-5 w-5" />
                              </Button>
                            </Tooltip>
                          </div>
                        )
                      }
                    />
                  );
                }}
              />
            </div>
          </OverlayScrollbarsComponent>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <Divider />
        <span className="text-lg font-medium">
          {t('modal.tagUserGroupMultiSelectForm.usersAndGroups')}
        </span>
        <OverlayScrollbarsComponent
          options={{
            scrollbars: {
              autoHide: 'leave',
            },
          }}
          defer
          style={{ height: '100%', overflow: 'auto' }}
        >
          <div
            ref={scrollableDivRef}
            //onScroll={handleScroll}
            className="mb-4 flex max-h-80 flex-col overflow-y-auto pr-2"
          >
            <ListyWithEmpty<TagUserGroupItem>
              items={tagUserGroupItems}
              loading={isLoading}
              emptyText={t('common.none')}
              rowKey={(item) => `${item.isGroup ? 'g' : 'u'}-${item.id}`}
              itemRender={(tagUserGroupItem) => {
                return (
                  <ListyMetaRow
                    avatar={
                      <Avatar
                        size={42}
                        className={clsx(
                          isTagUserGroupItemMarkedToRemove(tagUserGroupItem)
                            ? 'bg-red-dark!'
                            : 'bg-azure-hover!',
                          'uppercase!',
                        )}
                      >
                        {!tagUserGroupItem.isGroup ? (
                          getUserInitials(
                            tagUserGroupItem.firstName,
                            tagUserGroupItem.lastName,
                            tagUserGroupItem.userName,
                          )
                        ) : (
                          <Icon name="UsersIcon" className="h-5 w-5" />
                        )}
                      </Avatar>
                    }
                    title={
                      <span
                        className={clsx(
                          isTagUserGroupItemMarkedToRemove(tagUserGroupItem) && 'line-through',
                        )}
                      >
                        {tagUserGroupItem.isGroup
                          ? tagUserGroupItem.name
                          : `${tagUserGroupItem.firstName} ${tagUserGroupItem.lastName}`}
                      </span>
                    }
                    description={
                      <div className="flex h-6 flex-row items-center justify-between gap-2">
                        <Tooltip
                          title={
                            tagUserGroupItem.isGroup
                              ? tagUserGroupItem.name
                              : tagUserGroupItem.userName
                          }
                        >
                          <span className="truncate text-sm">
                            {tagUserGroupItem.isGroup
                              ? `${tagUserGroupItem.members} ${t('common.users')}`
                              : tagUserGroupItem.userName}
                          </span>
                        </Tooltip>
                      </div>
                    }
                    actions={
                      isTagUserGroupItemMarkedToRemove(tagUserGroupItem) ? (
                        <Button
                          variant="text"
                          color="primary"
                          size="small"
                          onClick={() =>
                            handleFormFieldChange(
                              'tagUserGroupItemsToRemove',
                              form.tagUserGroupItemsToRemove.filter(
                                (f) => f.id !== tagUserGroupItem.id,
                              ),
                            )
                          }
                        >
                          {t('common.cancel')}
                        </Button>
                      ) : (
                        <div className="flex flex-row items-center gap-0.5">
                          <Tooltip placement="top" title={t('common.remove')}>
                            <Button
                              onClick={() => {
                                handleFormFieldChange('tagUserGroupItemsToRemove', [
                                  ...form.tagUserGroupItemsToRemove,
                                  tagUserGroupItem,
                                ]);
                                // Remove the tag user group item from the form state without considering tagUserGroupItemsToRemove
                                setTagUserGroupItems(
                                  tagUserGroupItems.filter(
                                    (item) => item.id !== tagUserGroupItem.id,
                                  ),
                                );
                              }}
                              danger
                              type="text"
                              size="small"
                              aria-label={t('a11y.removeException')}
                            >
                              <Icon name="TrashIcon" className="h-5 w-5" />
                            </Button>
                          </Tooltip>
                        </div>
                      )
                    }
                  />
                );
              }}
            />
            {isLoading && (
              <>
                <Skeleton avatar paragraph={{ rows: 0 }} active />
                <Skeleton avatar paragraph={{ rows: 0 }} active />
                <Skeleton avatar paragraph={{ rows: 0 }} active />
                <Skeleton avatar paragraph={{ rows: 0 }} active />
                <Skeleton avatar paragraph={{ rows: 0 }} active />
              </>
            )}
          </div>
        </OverlayScrollbarsComponent>
      </div>

      <div className="mt-4 flex flex-row items-center justify-end">
        <div className="flex flex-row items-center justify-center gap-2">
          <Button size="middle" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button size="middle" disabled={isSubmitButtonDisabled} type="primary" htmlType="submit">
            {t('common.confirm')}
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default TagUserGroupMultiSelectForm;
